/**
 * ฉาก 0 ไม่มีไฟล์ภาพ — เป็น UI ที่ต้องวาดเป็น DOM (ASSETS.md §1)
 *
 * **ยึดหน้าตาจาก `mockups/player_you_walk.html` คลาส `.regui` ให้ตรง**
 * ตามที่ ASSETS.md §1 สั่งไว้ — ไม่ออกแบบใหม่ เพราะสิ่งที่ลูกค้าอนุมัติคือของในนั้น
 *
 * สองเรื่องที่ต้องระวังและเป็นเหตุผลของโครงในไฟล์นี้:
 *
 * 1. mockup วาดอยู่ในกรอบ 9:16 เป๊ะ และใช้หน่วย `cqw/cqh` (container query)
 *    ถ้าเอา UI ไปแปะเต็ม viewport บนจอ 16:9 สัดส่วนจะเพี้ยนจากที่ออกแบบไว้
 *    จึงต้องสร้างกรอบ 9:16 กลางจอให้ตรงกับที่ Phaser Scale.FIT ย่อ canvas ลงไป
 *
 * 2. ตัวละครที่วาร์ปลงฉาก 0 ถูกวาดด้วย Phaser บน canvas ซึ่งอยู่ **บน** DOM
 *    ใน mockup ตัวละครอยู่บน UI (z-index สูงกว่า) จึงต้องให้ canvas โปร่งใส
 *    แล้ววาง UI ไว้ข้างหลัง ไม่ใช่ทับหน้า — ดู `transparent` ใน phaser/index.ts
 */
import { qrToSvg } from "@py/engine";
import { GROUND_Y, SCENE_H } from "@py/protocol";

import { ENV } from "./env.js";

/**
 * QR ไม่หมดอายุแล้ว (DECISIONS.md D-27) — สแกนซ้ำได้ไม่จำกัด ถ่ายใหม่ได้เรื่อยๆ
 *
 * ยังขอโค้ดจาก API อยู่รอบเดียวตอนเปิดจอ เพื่อให้จอรู้ว่าต่อ API ได้จริง
 * ถ้าต่อไม่ได้จะได้ขึ้น error ให้คนหน้างานเห็น ไม่ใช่โชว์ QR ที่ยิงไปแล้วพัง
 *
 * ลองใหม่ทุก 30 วินาทีเฉพาะตอนที่ยังขอไม่สำเร็จ — สำเร็จแล้วหยุดยิง
 */
const RETRY_MS = 30_000;

/** เส้นพื้นร่วมทุกฉาก — ใน mockup คือ top:81.25% ซึ่งมาจากค่านี้ */
const FLOOR_PCT = (GROUND_Y / SCENE_H) * 100;

export type RegisterUi = { destroy: () => void };

export function mountRegisterUi(root: HTMLElement, present: boolean, debug = false): RegisterUi {
  const frame = document.createElement("div");
  frame.className = "regframe";
  frame.innerHTML = `
    <style>
      /* กรอบ 9:16 กลางจอ ให้ตรงกับที่ Phaser Scale.FIT ย่อ canvas ลงมา */
      .regframe {
        position: absolute; inset: 0;
        display: flex; align-items: center; justify-content: center;
      }
      .regui {
        position: relative;
        aspect-ratio: 9 / 16;
        height: 100%; max-height: 100%; max-width: 100%;
        container-type: size;
        display: flex; flex-direction: column; align-items: center;
        background: radial-gradient(130% 62% at 50% 20%, #16161f, #08080c 72%);
        overflow: hidden;
        /*
          ต้องตรงกับที่ mockup ตั้งไว้ที่ body เป๊ะ คือ
            font: 13px/1.5 "SF Mono", Menlo, Consolas, monospace
            letter-spacing: .02em
          เคยลองใช้ Cascadia Mono แล้วความกว้างตัวอักษรต่างไป หัวเรื่องกินพื้นที่ไม่เท่ากัน
          และถ้าปล่อย line-height เป็น normal (~1.2) กล่องข้อความทุกอันเตี้ยลง
          flex column จะดันทุกอย่างขึ้นสะสมกัน — วัดได้ว่าการ์ด QR เลื่อนขึ้น 50 px
          ห้ามเติมฟอนต์ไทยเข้ามา เพราะ mockup ปล่อยให้ระบบเลือกเอง
        */
        font-family: "SF Mono", Menlo, Consolas, monospace;
        line-height: 1.5;
        letter-spacing: .02em;
      }
      .rgrid {
        position: absolute; inset: 0;
        background-image:
          linear-gradient(rgba(200, 240, 0, .05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(200, 240, 0, .05) 1px, transparent 1px);
        background-size: 8cqw 8cqw;
      }
      /*
        mockup มีบรรทัด "bit.studio presents" อยู่บนสุด แต่เจ้าของงานสั่งถอดออก
        จึงบวกความสูงของบรรทัดนั้นคืนเข้า margin-top (2.6cqw × line-height 1.5
        = 3.9cqw ≈ 2.2cqh) เพื่อให้ QR และทุกอย่างด้านล่างอยู่ตำแหน่งเดิมเป๊ะ
      */
      .rtop { margin-top: 11.2cqh; text-align: center; z-index: 2; }
      .rtitle { font-size: 9cqw; letter-spacing: .2em; color: #fff; margin: 1.4cqw 0 .8cqw; }
      .rkick { font-size: 2.3cqw; letter-spacing: .26em; color: #7d7d8b; }
      .rcard {
        margin-top: 5.5cqh; width: 56cqw; aspect-ratio: 1;
        background: #fff; border-radius: 1.6cqw;
        display: flex; align-items: center; justify-content: center; z-index: 2;
        box-shadow: 0 0 0 .7cqw rgba(200, 240, 0, .18), 0 4cqw 9cqw rgba(0, 0, 0, .62);
      }
      .rcard svg, .rcard img { width: 88%; height: 88%; image-rendering: pixelated; }
      .rmid { margin-top: 3.4cqh; text-align: center; z-index: 2; }
      .rth { font-size: 3.4cqw; color: #e6e6ef; letter-spacing: .08em; }
      .rurl { font-size: 2.1cqw; color: #c8f000; letter-spacing: .06em; margin-top: 1.2cqw; }
      .rstep {
        margin-top: 3.4cqh; font-size: 2.1cqw; color: #7d7d8b; letter-spacing: .06em;
        display: flex; gap: 1.2cqw; align-items: center; justify-content: center;
        flex-wrap: wrap; padding: 0 4cqw;
      }
      .rstep span {
        display: inline-flex; width: 3.6cqw; height: 3.6cqw; border-radius: 50%;
        align-items: center; justify-content: center;
        background: #1b1b25; color: #c8f000; border: 1px solid #3a4413; font-size: 1.9cqw;
      }
      .rfloor {
        position: absolute; left: 0; right: 0;
        top: ${FLOOR_PCT}%; height: calc(100% - ${FLOOR_PCT}%);
        border-top: 2px solid rgba(200, 240, 0, .5); z-index: 2;
        background: linear-gradient(rgba(200, 240, 0, .055), transparent 45%);
        display: flex; align-items: flex-start; justify-content: space-between;
        padding: 1.8cqw 3cqw; font-size: 2cqw; letter-spacing: .14em;
      }
      .rfloor i { font-style: normal; color: #4e4e5c; }
      .rfloor b { font-weight: 400; color: #7d7d8b; }
      .rfloor u { text-decoration: none; color: #c8f000; }
      .rwaiting {
        position: absolute; left: 0; right: 0; top: ${FLOOR_PCT}%; z-index: 3;
        text-align: center; padding-top: 4cqw; font-size: 2.6cqw;
        letter-spacing: .2em; color: #5f5f6c; transition: opacity .4s;
      }
      .rerr { color: #ff8f8f; font-size: 2.2cqw; padding: 3cqw; text-align: center; }
    </style>
    <div class="regui">
      <div class="rgrid"></div>
      <div class="rtop">
        <div class="rtitle">PLAYER YOU</div>
        <div class="rkick">SCAN TO CREATE YOUR AVATAR</div>
      </div>
      <div class="rcard" id="rcard"></div>
      <div class="rmid">
        <div class="rth">สแกนด้วยกล้องมือถือ</div>
        <div class="rurl" id="rurl">&nbsp;</div>
        <div class="rstep">
          <span>1</span> ถ่ายรูปตัวเอง<span>2</span> ระบบสร้างตัวละคร<span>3</span> เดินเข้าฐานถัดไป
        </div>
      </div>
      <div class="rfloor"><i>FLOOR</i><b>STATION 00</b><u>ฐาน 1 &rarr;</u></div>
      <div class="rwaiting" id="rwaiting">&#9679; รอผู้เล่นลงทะเบียน</div>
    </div>
  `;
  root.appendChild(frame);

  const cardEl = frame.querySelector<HTMLElement>("#rcard")!;
  const urlEl = frame.querySelector<HTMLElement>("#rurl")!;
  const waitEl = frame.querySelector<HTMLElement>("#rwaiting")!;

  let alive = true;
  let retryTimer: ReturnType<typeof setInterval> | null = null;

  const stopRetry = () => {
    if (retryTimer) { clearInterval(retryTimer); retryTimer = null; }
  };

  async function showQr(): Promise<void> {
    if (!alive) return;
    try {
      const res = await fetch(`${ENV.apiBase}/api/station/0/nonce`, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { nonce } = (await res.json()) as { nonce: string };
      const base = ENV.mobileBase || location.origin;
      const url = `${base}/r/0?n=${encodeURIComponent(nonce)}`;
      cardEl.innerHTML = qrToSvg(url, { quiet: 2 });
      // แสดง URL แบบที่ mockup ทำ (ตัด https:// ออกให้อ่านง่ายบนจอไกลๆ)
      urlEl.textContent = url.replace(/^https?:\/\//, "");
      stopRetry();
    } catch (e) {
      // จอห้ามค้างเป็นหน้าว่าง — คนหน้างานต้องรู้ว่าต่อ API ไม่ได้
      cardEl.innerHTML = `<div class="rerr">ต่อ API ไม่ได้<br/>${String(e)}</div>`;
      urlEl.textContent = "—";
      if (!retryTimer) retryTimer = setInterval(() => void showQr(), RETRY_MS);
    }
  }

  void showQr();

  if (debug && !present) {
    waitEl.innerHTML = "&#9679; รอผู้เล่นลงทะเบียน · สแกนได้ไม่จำกัด";
  }

  return {
    destroy() {
      alive = false;
      stopRetry();
      frame.remove();
    },
  };
}
